/**
 * Tenant interceptor — attaches tenant and property headers to API requests.
 *
 * Resolution order:
 * 1. JWT access token claims (tenantId, propertyId)
 * 2. localStorage fallback (for unauthenticated or pre-login requests)
 *
 * The tenant ID is sent as `X-Tenant-ID` and property ID as `X-Property-ID`.
 */

const STORAGE_KEY_TENANT = 'tenant_id';
const STORAGE_KEY_PROPERTY = 'property_id';

/**
 * Decode a JWT payload without cryptographic verification.
 * This is safe for client-side header attachment since the server
 * independently verifies the token and extracts tenant context.
 */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payloadB64 = parts[1]
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    const padded = payloadB64 + '='.repeat((4 - (payloadB64.length % 4)) % 4);
    const json = atob(padded);
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * Attach tenant context headers to an outgoing API request.
 *
 * Priority:
 * 1. Extract tenantId/propertyId from the JWT access token
 * 2. Fall back to localStorage values for unauthenticated requests
 */
export function attachTenantHeaders(
  headers: Record<string, string>,
): Record<string, string> {
  if (typeof window === 'undefined') return headers;

  // 1. Try extracting from JWT token (authoritative source)
  const token = localStorage.getItem('access_token');
  if (token) {
    const payload = decodeJwtPayload(token);
    if (payload) {
      if (typeof payload.tenantId === 'string') {
        headers['X-Tenant-ID'] = payload.tenantId;
      }
      if (typeof payload.propertyId === 'string') {
        headers['X-Property-ID'] = payload.propertyId;
      }
      // If we got tenantId from JWT, return early — JWT is authoritative
      if (typeof payload.tenantId === 'string') {
        return headers;
      }
    }
  }

  // 2. Fall back to localStorage (unauthenticated requests, pre-login)
  const tenantId = localStorage.getItem(STORAGE_KEY_TENANT);
  const propertyId = localStorage.getItem(STORAGE_KEY_PROPERTY);

  if (tenantId) headers['X-Tenant-ID'] = tenantId;
  if (propertyId) headers['X-Property-ID'] = propertyId;

  return headers;
}
